import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetChip';
import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import { orderCoaches } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/coachingDashboardTabs';
import { EditMemberCoachesModal } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/EditMemberCoachesModal';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Avatar, Box, IconButton, type SxProps, type Theme, Typography } from '@mui/material';

import { useAgentContext } from './MemberCoachesSection.agentContext';

const {
  modules: { coachingDashboard },
  common: { button: BUTTON_STRINGS },
} = appStrings;

const COLLAPSED_COACH_COUNT = 3;

type MemberCoachesSectionProps = Readonly<{
  coaches: readonly CoachSummary[];
  memberId: number;
  memberName: string;
  canManageCoaches: boolean;
}>;

export const MemberCoachesSection = ({
  coaches,
  memberId,
  memberName,
  canManageCoaches,
}: MemberCoachesSectionProps) => {
  const navigate = useNavigate();
  const gateInSandbox = useSandboxGate();

  useAgentContext(coaches);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleOpenModal = useCallback(() => {
    if (gateInSandbox()) return;

    setIsModalOpen(true);
  }, [gateInSandbox]);

  const orderedCoaches = orderCoaches(coaches);
  const visibleCoaches = showAll ? orderedCoaches : orderedCoaches.slice(0, COLLAPSED_COACH_COUNT);
  const hasMore = orderedCoaches.length > COLLAPSED_COACH_COUNT;

  return (
    <Box sx={sectionSx}>
      <Box sx={headerSx}>
        <Typography variant="text-sm-medium" sx={titleSx}>
          {coachingDashboard.coachesTabTitle}
        </Typography>
        {canManageCoaches ? (
          <IconButton
            id="member-coaches-edit"
            className="member-coaches-edit"
            disableRipple
            onClick={handleOpenModal}
            aria-label={coachingDashboard.manageCoaches}
            sx={editButtonSx}
          >
            <AppIcon iconName="Edit05" diameter="16px" color="Colors/Foreground/fg-secondary" />
          </IconButton>
        ) : null}
      </Box>
      {coaches.length === 0 ? (
        <Typography variant="text-sm-regular" sx={emptyTextSx}>
          {coachingDashboard.noCoachesAssigned}
        </Typography>
      ) : (
        <Box sx={chipGroupSx}>
          {visibleCoaches.map((coach) => (
            <NuggetChip
              key={coach.id}
              id={`member-coach-${coach.id}`}
              label={personName(coach)}
              image={
                coach.profile_image_url
                  ? { src: coach.profile_image_url, alt: personName(coach) }
                  : undefined
              }
              icon={
                <Avatar id={`member-coach-initial-${coach.id}`} sx={initialSx}>
                  {personInitial(coach)}
                </Avatar>
              }
              onClick={() => navigate(`/profile/${coach.id}`)}
              sx={chipSx}
            />
          ))}
          {hasMore ? (
            <Typography
              id="member-coaches-toggle"
              variant="text-sm-semibold"
              onClick={() => setShowAll((prev) => !prev)}
              sx={toggleSx}
            >
              {showAll ? BUTTON_STRINGS.showLess : coachingDashboard.viewAllCoaches}
            </Typography>
          ) : null}
        </Box>
      )}
      {canManageCoaches ? (
        <EditMemberCoachesModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          memberId={memberId}
          memberName={memberName}
          currentCoaches={[...coaches]}
        />
      ) : null}
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-md'],
  '& .member-coaches-edit': { opacity: 0 },
  '&:hover .member-coaches-edit, & .member-coaches-edit:focus-visible': { opacity: 1 },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const editButtonSx: SxProps<Theme> = {
  padding: 0,
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: 'transparent' },
};

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const chipGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Base/Black'],
});

const initialSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-medium'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const toggleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  cursor: 'pointer',
});

const emptyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
